import { Check, ChevronsUpDown, CircleUser, RefreshCw } from 'lucide-react'
import {
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'
import { RepositorySelectorFallback } from '@/components/form/repository-selector/fallback'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery } from '@/lib/hooks'
import { cn, isPresent } from '@/lib/utils'
import type {
  GitSourcesQuery,
  GitSourcesQueryVariables,
} from '@/components/form/repository-selector/index.types.gen'

const GIT_REPOSITORIES = gql`
  query GitSources(
    $isGitHubNotSelected: Boolean!
    $isBitbucketNotSelected: Boolean!
    $isGitlabNotSelected: Boolean!
  ) {
    user {
      id
      githubRepositories: repositories(gitType: github)
        @live
        @skip(if: $isGitHubNotSelected) {
        id
        type
        idRemoteRepository
        name
        fullName
        avatarUrl
        url
        organization
        isPrivate
      }
      bitbucketRepositories: repositories(gitType: bitbucket)
        @live
        @skip(if: $isBitbucketNotSelected) {
        id
        type
        idRemoteRepository
        name
        fullName
        avatarUrl
        url
        organization
        isPrivate
      }
      gitlabRepositories: repositories(gitType: gitlab)
        @live
        @skip(if: $isGitlabNotSelected) {
        id
        type
        idRemoteRepository
        name
        fullName
        avatarUrl
        url
        organization
        isPrivate
      }
    }
  }
`

type FormValues = {
  gitType: 'github' | 'gitlab' | 'bitbucket' | null
  idRemoteRepository: string | null
  repoUrl: string | null
  displayName: string | null
  defaultBranch: string | null
}

const RepositorySelectorContent = () => {
  const [repositoryOpen, setRepositoryOpen] = useState(false)
  const form = useFormContext<FormValues>()
  const { watch, setValue, resetField, clearErrors } = form

  const gitType = watch('gitType')
  const idRemoteRepository = watch('idRemoteRepository')

  const { data, refetch } = useSuspenseQuery<
    GitSourcesQuery,
    GitSourcesQueryVariables
  >(GIT_REPOSITORIES, {
    variables: {
      isGitHubNotSelected: gitType !== 'github',
      isBitbucketNotSelected: gitType !== 'bitbucket',
      isGitlabNotSelected: gitType !== 'gitlab',
    },
  })

  const repositories = useMemo(
    () =>
      ({
        github: (data?.user?.githubRepositories ?? []).filter(isPresent),
        bitbucket: (data?.user?.bitbucketRepositories ?? []).filter(isPresent),
        gitlab: (data?.user?.gitlabRepositories ?? []).filter(isPresent),
      })[gitType ?? 'github'] ?? [],
    [data, gitType]
  )

  const chosenRepository = useMemo(
    () =>
      repositories.find(
        (repo) => repo?.idRemoteRepository === idRemoteRepository
      ),
    [idRemoteRepository, repositories]
  )

  const onRepositorySearch = useCallback(
    (value: string, search: string): 1 | 0 => {
      const repository = repositories.find(
        (repo) => repo.idRemoteRepository.toString() === value.toString()
      )

      if (!repository) return 0

      const fullName = `${repository.organization}/${repository.name}`
      return fullName.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
    },
    [repositories]
  )

  useEffect(() => {
    if (chosenRepository) {
      clearErrors('displayName')
      setValue('displayName', chosenRepository.name ?? '')
    }
  }, [chosenRepository, setValue, clearErrors])

  useEffect(() => {
    return () => {
      resetField('idRemoteRepository')
    }
  }, [resetField])

  const RepositoryComboBox = ({ children }: { children: ReactNode }) => (
    <FormField
      control={form.control}
      name="idRemoteRepository"
      render={({ field }) => (
        <FormItem className="w-full min-w-0">
          <FormLabel>Repository</FormLabel>
          <FormControl>
            <div className="flex flex-col gap-4 w-full min-w-0">
              <Popover
                open={repositoryOpen}
                onOpenChange={setRepositoryOpen}
                modal
              >
                <div className="grid grid-cols-[1fr_auto] gap-4">
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={repositoryOpen}
                      className="w-full justify-between hover:bg-transparent data-[state=open]:bg-transparent min-w-0"
                    >
                      {chosenRepository ? (
                        <div className="flex items-center justify-between w-full gap-2">
                          <div className="flex items-center gap-2 w-full min-w-0">
                            <Avatar className="inline-block size-4">
                              <AvatarImage
                                src={chosenRepository.avatarUrl ?? ''}
                                title={`${chosenRepository.organization}/${chosenRepository.name}`}
                              />
                              <AvatarFallback>
                                <CircleUser />
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate min-w-0">
                              {chosenRepository.organization}/
                              {chosenRepository.name}
                            </span>
                          </div>
                          <ChevronsUpDown className="ml-auto opacity-50" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between w-full">
                          Select a repository
                          <ChevronsUpDown className="ml-auto opacity-50" />
                        </div>
                      )}
                    </Button>
                  </PopoverTrigger>
                  {children}
                </div>
                <PopoverContent className="w-[var(--radix-popper-anchor-width)] p-0">
                  <Command filter={onRepositorySearch}>
                    <CommandInput
                      placeholder="Search repository..."
                      className="h-9"
                    />
                    <CommandList>
                      <CommandEmpty>No repository found</CommandEmpty>
                      <CommandGroup>
                        {repositories.map((repository) => {
                          const isSelected =
                            chosenRepository?.idRemoteRepository ===
                            repository.idRemoteRepository
                          return (
                            <CommandItem
                              className="cursor-pointer"
                              key={repository.idRemoteRepository}
                              disabled={isSelected}
                              value={repository.idRemoteRepository}
                              onSelect={() => {
                                field.onChange(repository.idRemoteRepository)
                                setValue('repoUrl', repository.url ?? null)
                                setValue('defaultBranch', '')
                                setRepositoryOpen(false)
                              }}
                            >
                              <Avatar className="size-4">
                                <AvatarImage
                                  src={repository.avatarUrl ?? ''}
                                  title={`${repository.organization}/${repository.name}`}
                                />
                                <AvatarFallback>
                                  <CircleUser />
                                </AvatarFallback>
                              </Avatar>
                              <span
                                className="truncate"
                                title={`${repository.organization}/${repository.name}`}
                              >
                                {repository.organization}/{repository.name}
                              </span>
                              <Check
                                className={cn(
                                  'ml-auto',
                                  isSelected ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                            </CommandItem>
                          )
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )

  return (
    <RepositoryComboBox>
      <Button
        variant="secondary"
        type="button"
        size="icon"
        aria-label="Refresh repositories"
        onClick={() => refetch()}
      >
        <RefreshCw className="size-4" />
      </Button>
    </RepositoryComboBox>
  )
}

export const RepositorySelector = () => {
  return (
    <Suspense fallback={<RepositorySelectorFallback />}>
      <RepositorySelectorContent />
    </Suspense>
  )
}
